/**
 * IndexedDB `mushaf`, version 2 — §13.1, §13.2.
 *
 * WRITE-THROUGH, NOT WRITE-BEHIND. A hifz grade or a note must survive closing the
 * tab immediately afterwards, and the volumes are far too small for batching to be
 * worth the lost durability.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import {
  DB_NAME,
  DB_VERSION,
  type Bookmark,
  type Note,
  type PageState,
  type Program,
  type SrsCard,
} from '@mushaf/corpus'

interface MushafDB extends DBSchema {
  notes: { key: string; value: Note }
  bookmarks: { key: string; value: Bookmark }
  srs: { key: number; value: SrsCard }
  pages: { key: number; value: PageState }
  programs: { key: string; value: Program }
  kv: { key: string; value: unknown }
}

export type StoreName = 'notes' | 'bookmarks' | 'srs' | 'pages' | 'programs' | 'kv'
export const ALL_STORES: StoreName[] = ['notes', 'bookmarks', 'srs', 'pages', 'programs', 'kv']

let dbPromise: Promise<IDBPDatabase<MushafDB>> | null = null

export function getDb(): Promise<IDBPDatabase<MushafDB>> {
  if (dbPromise) return dbPromise

  dbPromise = (async () => {
    const db = await openDB<MushafDB>(DB_NAME, DB_VERSION, {
      upgrade(database, oldVersion) {
        // A fresh build starts at v2; the branches below exist so a user upgrading
        // from the previous app is not stranded. IndexedDB HAS NO DOWNGRADE — a user
        // on v2 who reverts the app sees the v1 open fail. Accepted for a static SPA
        // with no staged rollout (§13.2).
        if (oldVersion < 1) {
          database.createObjectStore('notes', { keyPath: 'verseKey' })
          database.createObjectStore('bookmarks', { keyPath: 'verseKey' })
          database.createObjectStore('srs', { keyPath: 'page' })
          database.createObjectStore('pages', { keyPath: 'page' })
          database.createObjectStore('kv')
        }
        if (oldVersion < 2) {
          if (!database.objectStoreNames.contains('programs')) {
            database.createObjectStore('programs', { keyPath: 'id' })
          }
          if (!database.objectStoreNames.contains('kv')) {
            database.createObjectStore('kv')
          }
        }
      },
    })

    /**
     * THE DATA HALF OF A MIGRATION CANNOT RUN INSIDE `upgrade` (§13.2).
     *
     * A `versionchange` transaction closes the moment control returns to the event
     * loop, so reading another store to seed a cursor is not safe to await there. Run
     * it here — immediately after open, before the DB promise resolves — so no caller
     * can observe a half-migrated state. It is idempotent: the old key is its own
     * "not done yet" flag, so a tab closed mid-migration simply runs it again.
     */
    await migrateLegacyKhatmah(db)
    return db
  })()

  return dbPromise
}

const LEGACY_KHATMAH_KEY = 'khatmah'

async function migrateLegacyKhatmah(db: IDBPDatabase<MushafDB>): Promise<void> {
  const legacy = (await db.get('kv', LEGACY_KHATMAH_KEY)) as
    { pagesPerDay?: number; startedOn?: number; cursorPage?: number } | undefined
  if (!legacy) return

  const id = 'khatmah-legacy'
  const existing = await db.get('programs', id)
  if (!existing) {
    const startedOn = legacy.startedOn ?? Math.floor(Date.now() / 86_400_000)
    const program: Program = {
      id,
      methodId: 'tilawah',
      name: 'ختمة تلاوة',
      kind: 'tilawah',
      scope: { type: 'mushaf' },
      volumes: [{ from: startedOn, unit: 'page', amount: legacy.pagesPerDay ?? 4, review: 0 }],
      cursor: { page: legacy.cursorPage ?? 1, line: 1 },
      // The migrated legacy khatmah sets startAt to page 1 so it keeps reporting the
      // same fraction of the mushaf it always did (§11.2).
      startAt: { page: 1, line: 1 },
      restDays: [],
      maxCarryOver: 3,
      state: 'active',
      startedOn,
      createdAt: Date.now(),
    }
    await db.put('programs', program)
    await db.put('kv', id, 'activeProgram')
  }
  await db.delete('kv', LEGACY_KHATMAH_KEY)
}

// ---------------------------------------------------------------------------
// Snapshot / replace
// ---------------------------------------------------------------------------

export interface LibrarySnapshot {
  notes: Note[]
  bookmarks: Bookmark[]
  srs: SrsCard[]
  pages: PageState[]
  programs: Program[]
  activeProgram: string | null
}

export async function readSnapshot(): Promise<LibrarySnapshot> {
  const db = await getDb()
  const [notes, bookmarks, srs, pages, programs, activeProgram] = await Promise.all([
    db.getAll('notes'),
    db.getAll('bookmarks'),
    db.getAll('srs'),
    db.getAll('pages'),
    db.getAll('programs'),
    db.get('kv', 'activeProgram') as Promise<string | undefined>,
  ])
  return { notes, bookmarks, srs, pages, programs, activeProgram: activeProgram ?? null }
}

/**
 * Import REPLACES EVERYTHING IN ONE TRANSACTION ACROSS ALL STORES (§13.3).
 *
 * A half-applied import leaving notes from one device and SRS state from another is
 * worse than a clean failure.
 */
export async function replaceAll(snapshot: LibrarySnapshot): Promise<void> {
  const db = await getDb()
  const tx = db.transaction(ALL_STORES, 'readwrite')
  await Promise.all([
    tx.objectStore('notes').clear(),
    tx.objectStore('bookmarks').clear(),
    tx.objectStore('srs').clear(),
    tx.objectStore('pages').clear(),
    tx.objectStore('programs').clear(),
    tx.objectStore('kv').clear(),
  ])
  await Promise.all([
    ...snapshot.notes.map((n) => tx.objectStore('notes').put(n)),
    ...snapshot.bookmarks.map((b) => tx.objectStore('bookmarks').put(b)),
    ...snapshot.srs.map((c) => tx.objectStore('srs').put(c)),
    ...snapshot.pages.map((p) => tx.objectStore('pages').put(p)),
    ...snapshot.programs.map((p) => tx.objectStore('programs').put(p)),
    tx.objectStore('kv').put(snapshot.activeProgram, 'activeProgram'),
  ])
  await tx.done
}

export async function put<T>(store: Exclude<StoreName, 'kv'>, value: T): Promise<void> {
  const db = await getDb()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await db.put(store as any, value as any)
}

export async function remove(store: Exclude<StoreName, 'kv'>, key: string | number): Promise<void> {
  const db = await getDb()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await db.delete(store as any, key as any)
}

export async function putKv(key: string, value: unknown): Promise<void> {
  const db = await getDb()
  await db.put('kv', value, key)
}
